import { useEffect, useMemo, useRef, useState } from 'react'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useParams } from 'react-router'

import { useFetchList, useReload } from 'store'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import useSectionTitle from 'hooks/useSectionTitle'

import RulesEditor from './RulesEditor'
import styles from './RulesEditorController.module.sass'
import useRulesMetadata from './useRulesMetadata'
import {
	fields,
	output,
	getRulesSchema,
	numericOperators,
	listOperators,
	TaskTypes,
	RuleTypes,
	coordinators,
} from './RuleSchema'
import Rule from 'models/Rule'
import { translateErrorMessage } from 'hooks/useSubmitTask'

const FORM_ID = 'EDITOR_FORM_ID'

const availableFields = (i18n) =>
	fields.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableNumericOperators = (i18n) =>
	numericOperators.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableListOperators = (i18n) =>
	listOperators.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableCoordinators = (i18n) =>
	coordinators.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableOutput = (i18n) =>
	output.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const UsersRulesEditorController = (props) => {
	const { userRules, userId } = props
	const { i18n, expireSession, pushNotification } = useGlobal()
	const title = useSectionTitle(i18n, 'taskType')
	const { ruleType = props.ruleType, taskType = props.taskType } = useParams()
	const userRef = useRef()

	const apiEndpoint = `/rules?ruleType=${RuleTypes[ruleType]}&taskType=${TaskTypes[taskType]}`
	const [{ endpoint, initialData }, setInitialData] = useState({
		endpoint: '',
		initialData: [],
	})
	const [submitError, setSubmitError] = useState(null)

	const metadata = useRulesMetadata()
	const reload = useReload()
	const { contents, state } = useFetchList([apiEndpoint], [Rule.fromJSON])

	const filteredRules = useMemo(() => {
		if (userRules) {
			return contents.map((rules) => {
				return Array.isArray(rules)
					? rules.filter(
							(rule) =>
								rule.ruleType === 'UserExclusionRule' &&
								rule?.output?.userId === userId
					  )
					: rules
			})
		} else {
			return contents
		}
	}, [contents, userId, userRules])

	const rulesRaw = useMemo(
		() =>
			state === 'hasValue' && Array.isArray(filteredRules[0])
				? filteredRules[0]
				: [],
		[filteredRules, state]
	)

	const defaultValues = {
		rules: Array.isArray(filteredRules[0])
			? filteredRules[0].sort((a, b) => a.order - b.order)
			: [],
	}

	const fieldsOptions = useMemo(() => availableFields(i18n), [i18n])
	const numericOperatorsOptions = useMemo(
		() => availableNumericOperators(i18n),
		[i18n]
	)
	const listOperatorsOptions = useMemo(
		() => availableListOperators(i18n),
		[i18n]
	)
	const coordinatorOptions = useMemo(
		() => availableCoordinators(i18n),
		[i18n]
	)
	const outputOptions = useMemo(() => availableOutput(i18n), [i18n])
	const options = {
		...metadata,
		fields: fieldsOptions,
		numericOperators: numericOperatorsOptions,
		listOperators: listOperatorsOptions,
		outputOptions,
		coordinatorOptions,
	}

	const methods = useForm({
		resolver: zodResolver(getRulesSchema(options, i18n)),
		shouldUseCustomValidity: true,
		defaultValues,
	})

	const { reset, formState, handleSubmit } = methods

	useEffect(
		() => () => {
			setInitialData({ endpoint: '', initialData: [] })
		},
		[]
	)

	useEffect(() => {
		if (
			(endpoint !== apiEndpoint || userRef.current !== userId) &&
			state === 'hasValue' &&
			Array.isArray(filteredRules[0])
		) {
			userRef.current = userId
			setInitialData({
				endpoint: apiEndpoint,
				initialData: filteredRules[0],
			})
		}
	}, [
		filteredRules.length,
		endpoint,
		rulesRaw,
		state,
		filteredRules,
		apiEndpoint,
		userId,
	])

	useEffect(() => {
		reset({ rules: initialData })
	}, [initialData, reset])

	const onSubmit = (formData) => {
		const { rules } = formData

		const userRules = rules.map((ele) => ({
			...ele,
			output: { userId },
		}))

		const matchedRules = contents[0].filter(
			(ele) =>
				ele.output?.userId !== userId &&
				ele.taskType === TaskTypes[taskType]
		)
		const newRules = [...userRules, ...matchedRules]

		const rulesForSubmission = newRules.map((rule) => Rule.toJSON(rule))

		setSubmitError(null)
		const endpoint = `${API_URL}/api/rules`

		return fetch(
			endpoint,
			fetchOptions({
				method: 'PUT',
				body: {
					ruleType: RuleTypes[ruleType],
					taskType: TaskTypes[taskType],
					rules: rulesForSubmission,
				},
			})
		)
			.then((r) => parseJSONContent(r))
			.then((r) => {
				setSubmitError(null)
				reload([apiEndpoint])
				pushNotification(
					undefined,
					'success',
					i18n`Rule created successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					expireSession()
				} else {
					const errorMessage = translateErrorMessage(
						e?.formErrors?.[0] ?? e?.fieldErrors?.[0] ?? e,
						i18n
					)

					setSubmitError(errorMessage)
				}
			})
	}

	return (
		<>
			<span className={styles['background']} />
			<div
				className={`${styles['actions-container']} ${
					formState.isSubmitting ? styles['--is-submitting'] : ''
				} ${submitError ? styles['--has-error'] : ''}`}
			>
				{formState.isSubmitting && (
					<span className={styles['progress-background']} />
				)}
				<HStack justify='space-between'>
					<span>
						<h5>{title}</h5>
						<small>
							{formState.isSubmitting
								? i18n`Submitting`
								: submitError ??
								  i18n`Update will only take effect on future tasks`}
						</small>
					</span>
					<HStack gap='small'>
						<ActionButton
							type='button'
							onClick={() => {
								const isDirty =
									Object.entries(formState.dirtyFields)
										.length > 0
								if (isDirty) {
									reset(defaultValues)
								}
							}}
						>
							{i18n`Reset`}
						</ActionButton>
						<ActionButton
							primary
							type='submit'
							form={FORM_ID}
							disabled={formState.isSubmitting}
						>
							{formState.isSubmitting
								? i18n`Submitting`
								: i18n`Save`}
						</ActionButton>
					</HStack>
				</HStack>
			</div>
			<div className={styles['editor']}>
				<FormProvider {...methods}>
					<RulesEditor
						id={FORM_ID}
						scrollableClassName={styles['editor']}
						ruleType={RuleTypes[ruleType]}
						taskType={TaskTypes[taskType]}
						options={options}
						onSubmit={handleSubmit(onSubmit)}
					/>
				</FormProvider>
			</div>
		</>
	)
}

export default UsersRulesEditorController
